import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";
import * as yup from "yup";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import { checkArray, checkNull, parseStringToDate } from "../../../helpers";
import {
  accountManagementData,
  cancelAccount,
  changeSettingValue,
  getPaymentOwnerSubAccounts,
  getState,
  saveAccountManagement,
  savePaymentInfo,
  updateAccountAdmin,
  updatePlanCost,
  updatePlanTopup,
  updatePosCount,
} from "../../../Redux/actions";
import Headerbar from "../../Components/InnerLayout/Headerbar";
import UserInterface from "../../Components/InnerLayout/UserInterface";
import moment from "moment";
import MyGoogleMap from "../../Components/GoogleMap/MyGoogleMap";
import ErrorContainer from "../../Components/ErrorContainer";
import ModelContainer from "../../Components/ModelContainer";
import ConfirmModel from "../../Components/ConfirmModel";
import LoadingBtn from "../../Components/LoadingBtn";
import MultipleValueTextInput from "react-multivalue-text-input";

const schema = yup
  .object({
    company_name: yup.string().required(),
    company_name: yup.string().required(),
    account_name: yup.string().required(),
    timezone: yup.string().required(),
    zip_code: yup.string().required(),
    address: yup.string().required(),
    address_2: yup.string().nullable(),
    country_id: yup.string().required(),
    city_id: yup.string().required(),
    state_id: yup.string().required(),
    latitude: yup.string().required(),
    longitude: yup.string().required(),
  })
  .required();

const accAdminSchema = yup
  .object({
    admin_id: yup.string().nullable().required(),
    group_id: yup.string().nullable().required(),
  })
  .required();

const savePaymentSchema = yup
  .object({
    account_owner: yup.string().nullable().required(),
    payment_account: yup.string().nullable().required(),
    landlord_name: yup.string().nullable().required(),
    delivery_type: yup.string().nullable().required(),
    email_address: yup.array().when("delivery_type", {
      is: "1",
      then: yup
        .array()
        .of(yup.string().email(({ value }) => `${value} is not a valid email`))
        .required()
        .min(1),
    }),
    sftp_address: yup.string().nullable().when("delivery_type", {
      is: "2",
      then: yup.string().nullable().required(),
    }),
    sftp_user: yup.string().nullable().when("delivery_type", {
      is: "2",
      then: yup.string().nullable().required(),
    }),
    sftp_password: yup.string().nullable().when("delivery_type", {
      is: "2",
      then: yup.string().nullable().required(),
    }),
    tenant_id: yup.string().nullable().required(),
    bssn: yup.number().positive().required(),
  })
  .required();

const AccountManagement = (params) => {
  const history = useHistory();
  const dispatch = useDispatch();
  const {
    account_management,
    is_add_btn_loading,
    is_accAdmin_modal_loading,
    accAdminModal,
    changePlanModal,
    is_changePlan_modal_loading,
    changeTopupModal,
    is_changeTopup_modal_loading,
    changePosCountModal,
    is_changePosCount_modal_loading,
    is_save_payment_loading,
    is_cancel_account_loading,
    cancelAccModal,
  } = useSelector((state) => state.settingReducer);
  const user_details = useSelector((state) => state.authReducer.user_details);
  const { states: apiStates } = useSelector((state) => state.authReducer);

  const accAdminInitialState = {
    admin_id: "",
    group_id: "",
  };
  const savePaymentInitialState = {
    account_owner: "",
    payment_account: "",
    landlord_name: "",
    delivery_type: "",
    email_address: [],
    sftp_address: "",
    sftp_user: "",
    sftp_password: "",
    tenant_id: "",
    bssn: "",
  };
  const initialForm = {
    company_name: "",
    account_name: "",
    timezone: "",
    zip_code: "",
    address: "",
    address_2: "",
    country_id: "",
    city_id: "",
    state_id: "",
    latitude: 0,
    longitude: 0,
    is_parent: "0",
    is_group_admin: "0",
    enable_cluster: "0",
  };

  const [accAdminForm, setAccAdminForm] = useState(accAdminInitialState);
  const [inputForm, setInputForm] = useState(initialForm);
  const [countryArr, setCountryArr] = useState([]);
  const [stateArr, setStateArr] = useState([]);
  const [showGroupList, setShowGroupList] = useState(false);
  const [posPlanCostId, setPosPlanCostId] = useState(null);
  const [currentPlanCost, setCurrentPlanCost] = useState(null);
  const [planId, setPlanId] = useState(null);
  const [posCount, setPosCount] = useState(null);
  const [posPlan, setPosPlan] = useState(null);

  const [savePaymentForm, setSavePaymentForm] = useState(
    savePaymentInitialState
  );
  const savePaymentBtn = useRef(null);

  const closeCancelAccModal = useCallback(
    (value) => {
      dispatch(changeSettingValue("cancelAccModal", value));
    },
    [cancelAccModal]
  );
  const submitCancelAccHandler = () => {
    dispatch(cancelAccount({ merchant_id: user_details?.user?.merchant_id }));
  };

  useEffect(() => {
    if (accAdminForm?.admin_id !== "") {
      setShowGroupList(true);
    } else {
      setShowGroupList(false);
    }
  }, [accAdminForm?.admin_id]);

  // useEffect(() => {}, [account_management?.account?.GtoReporting]);

  const {
    register: savePaymentReg,
    formState: { errors: savePaymentErrors },
    handleSubmit: savePaymentHandleSubmit,
    control: savePaymentControl,
    setValue: savePaymentSetValue,
    reset: savePaymentReset,
  } = useForm({
    resolver: yupResolver(savePaymentSchema),
    defaultValues: savePaymentInitialState,
    mode: "all",
    reValidateMode: "onChange",
  });

  const {
    register: accAdminReg,
    formState: { errors: accAdminErrors },
    handleSubmit: accAdminHandleSubmit,
    control: accAdminControl,
    setValue: accAdminSetValue,
    reset: accAdminReset,
  } = useForm({
    resolver: yupResolver(accAdminSchema),
    defaultValues: accAdminInitialState,
    mode: "all",
    reValidateMode: "onChange",
  });

  const {
    register,
    formState: { errors },
    handleSubmit,
    control,
    setValue,
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: initialForm,
    mode: "all",
    reValidateMode: "onChange",
  });

  const initialAccAdminModalState = () => {
    setAccAdminForm(accAdminInitialState);
    accAdminReset(accAdminInitialState);
  };

  const initialSetupPaymentState = () => {
    setSavePaymentForm(savePaymentInitialState);
    savePaymentReset(savePaymentInitialState);
  };

  const closeAccAdminModal = useCallback(
    (value) => {
      dispatch(changeSettingValue("accAdminModal", value));
      initialAccAdminModalState();
    },
    [accAdminModal]
  );
  const closeChangePlanModal = useCallback(
    (value) => {
      dispatch(changeSettingValue("changePlanModal", value));
      setPosPlanCostId(
        account_management?.account?.merchant?.pos_plan_cost_id ?? null
      );
      setCurrentPlanCost(
        account_management?.account?.merchant?.pos_plan_cost?.cost ?? null
      );
    },
    [changePlanModal, account_management]
  );
  const closeChangeTopupModal = useCallback(
    (value) => {
      dispatch(changeSettingValue("changeTopupModal", value));
      setPlanId(
        account_management?.account?.merchant?.pos_plan_cost?.id ?? null
      );
    },
    [changeTopupModal, account_management]
  );
  const closeChangePosCountModal = useCallback(
    (value) => {
      dispatch(changeSettingValue("changePosCountModal", value));
      setPosPlan(
        account_management?.account?.merchant?.pos_plan_cost?.id ?? null
      );
      setPosCount(
        account_management?.account?.merchant?.total_pos_terminals ?? 0
      );
    },
    [changePosCountModal, account_management]
  );

  const adminAccSubmitHandler = (data) => {
    data.user_id = account_management?.user_id;
    console.log("adminAccSubmitHandler", data);
    dispatch(updateAccountAdmin(data));
  };

  const changeInputHandler = (input, val, getStateApi = true) => {
    setValue(input, val);
    setInputForm((prevState) => ({ ...prevState, [input]: val }));
    if (getStateApi && input == "country_id") {
      const stateQuery = checkNull(val, false) ? `?country_id=${val}` : "";
      dispatch(getState(stateQuery));
      changeInputHandler("state_id", "");
    }
  };
  const changeAccAdminInputHandler = (input, val) => {
    accAdminSetValue(input, val);
    setAccAdminForm((prevState) => ({ ...prevState, [input]: val }));
  };
  const changeSavePaymentInputHandler = (input, val, getSubAccApi = true) => {
    savePaymentSetValue(input, val);
    setSavePaymentForm((prevState) => ({ ...prevState, [input]: val }));
    if (getSubAccApi && input == "account_owner") {
      if (checkNull(val)) dispatch(getPaymentOwnerSubAccounts(val));
      else
        dispatch(
          changeSettingValue("account_management", {
            ...account_management,
            subAccounts: [],
          })
        );
    }
  };

  useEffect(() => {
    if (apiStates && apiStates !== null) {
      setStateArr(apiStates);
    }
  }, [apiStates]);

  useEffect(() => {
    setCountryArr(account_management?.countries ?? []);
    setStateArr(account_management?.states ?? []);
    changeInputHandler(
      "is_parent",
      account_management?.account?.is_parent
        ? `${account_management?.account?.is_parent}`
        : "0"
    );
    changeInputHandler(
      "is_group_admin",
      account_management?.account?.merchant?.is_group_admin
        ? `${account_management?.account?.merchant?.is_group_admin}`
        : "0"
    );
    changeInputHandler(
      "enable_cluster",
      account_management?.account?.merchant?.enable_cluster
        ? `${account_management?.account?.merchant?.enable_cluster}`
        : "0"
    );
    changeInputHandler(
      "company_name",
      account_management?.account?.merchant?.company_name ?? ""
    );
    changeInputHandler(
      "account_name",
      account_management?.account?.merchant?.account_name ?? ""
    );
    changeInputHandler("timezone", account_management?.account?.timezone ?? "");
    changeInputHandler(
      "address",
      account_management?.account?.merchant?.address ?? ""
    );
    changeInputHandler(
      "address_2",
      account_management?.account?.merchant?.address_2 ?? ""
    );
    changeInputHandler(
      "country_id",
      account_management?.account?.merchant?.country_id ?? "",
      false
    );
    changeInputHandler(
      "state_id",
      account_management?.account?.merchant?.state_id ?? ""
    );
    changeInputHandler(
      "city_id",
      account_management?.account?.merchant?.city_id ?? ""
    );
    changeInputHandler(
      "zip_code",
      account_management?.account?.merchant?.zip_code ?? ""
    );
    setPosPlanCostId(
      account_management?.account?.merchant?.pos_plan_cost_id ?? null
    );
    setCurrentPlanCost(
      account_management?.account?.merchant?.pos_plan_cost?.cost ?? null
    );
    setPlanId(account_management?.account?.merchant?.pos_plan_cost?.id ?? null);
    setPosCount(
      account_management?.account?.merchant?.total_pos_terminals ?? 0
    );
    if (
      checkNull(account_management?.account?.GtoReporting?.delivery_type, false)
    ) {
      changeSavePaymentInputHandler(
        "delivery_type",
        account_management?.account?.GtoReporting?.delivery_type
      );
    }
    if (
      checkNull(account_management?.account?.GtoReporting?.email_address, false)
    ) {
      changeSavePaymentInputHandler(
        "email_address",
        account_management?.account?.GtoReporting?.email_address.split(",")
      );
    }
    if (
      checkNull(account_management?.account?.GtoReporting?.landlord_name, false)
    ) {
      changeSavePaymentInputHandler(
        "landlord_name",
        account_management?.account?.GtoReporting?.landlord_name
      );
    }
    if (
      checkNull(account_management?.account?.GtoReporting?.sftp_address, false)
    ) {
      changeSavePaymentInputHandler(
        "sftp_address",
        account_management?.account?.GtoReporting?.sftp_address
      );
    }
    if (
      checkNull(account_management?.account?.GtoReporting?.sftp_password, false)
    ) {
      changeSavePaymentInputHandler(
        "sftp_password",
        account_management?.account?.GtoReporting?.sftp_password
      );
    }
    if (
      checkNull(account_management?.account?.GtoReporting?.sftp_user, false)
    ) {
      changeSavePaymentInputHandler(
        "sftp_user",
        account_management?.account?.GtoReporting?.sftp_user
      );
    }
    if (
      checkNull(account_management?.account?.GtoReporting?.tenant_id, false)
    ) {
      changeSavePaymentInputHandler(
        "tenant_id",
        account_management?.account?.GtoReporting?.tenant_id
      );
    }
    if (checkNull(account_management?.account?.GtoReporting?.bssn, false)) {
      changeSavePaymentInputHandler(
        "bssn",
        account_management?.account?.GtoReporting?.bssn
      );
    }
    if (checkNull(account_management?.account?.GtoReporting?.bssn, false)) {
      changeSavePaymentInputHandler(
        "bssn",
        account_management?.account?.GtoReporting?.bssn
      );
    }
    if (
      checkNull(account_management?.account?.merchant?.account_owner, false)
    ) {
      changeSavePaymentInputHandler(
        "account_owner",
        account_management?.account?.merchant?.account_owner,
        false
      );
    }
    if (
      checkNull(account_management?.account?.merchant?.payment_account, false)
    ) {
      changeSavePaymentInputHandler(
        "payment_account",
        account_management?.account?.merchant?.payment_account
      );
    }
  }, [
    account_management?.account,
    account_management?.countries,
    account_management?.states,
  ]);

  const mapChangeHandler = (values) => {
    console.log("values", values);
    changeInputHandler("address", values?.address_line ?? "");
    if (
      values?.country &&
      Object.values(countryArr).indexOf(values?.country) > -1
    ) {
      Object.keys(countryArr).map((index) => {
        if (countryArr?.[index] == values?.country) {
          changeInputHandler("country_id", index);
        }
      });
    }
    // changeInputHandler("city_id", values?.city ?? "");
    changeInputHandler("zip_code", values?.zip_code ?? "");
    changeInputHandler("latitude", values?.lat ?? "");
    changeInputHandler("longitude", values?.lng ?? "");
  };

  useEffect(() => {
    dispatch(
      accountManagementData(
        user_details?.user?.merchant_id,
        user_details?.user?.id
      )
    );
    return () => {
      dispatch(changeSettingValue("account_management", null));
    };
  }, []);

  const onFormSubmit = (data) => {
    console.log("sub_data:", data);
    data.id = user_details?.user?.merchant_id;
    dispatch(saveAccountManagement(data));
  };
  const onSavePaymentFormSubmit = (data) => {
    data.merchant_id = account_management?.merchant_id;
    if (checkArray(data?.email_address)) {
      data.email_address = data.email_address.join(",");
    } else {
      data.email_address = "";
    }
    if (checkNull(account_management?.account?.GtoReporting?.id, false)) {
      data.gto_reporting_id = account_management?.account?.GtoReporting?.id;
    }
    console.log("onSavePaymentFormSubmit:", data);
    dispatch(savePaymentInfo(data));
  };

  const changePlanSubmitHandler = () => {
    console.log("changePlanSubmitHandler:");
    const updatePlanData = {
      merchant_id: account_management?.account?.merchant_id,
      pos_plan_cost_id: posPlanCostId,
      current_plan_cost: currentPlanCost,
    };
    dispatch(updatePlanCost(updatePlanData));
  };
  const changeTopupSubmitHandler = () => {
    console.log("changeTopupSubmitHandler:");
    const updateTopupData = {
      merchant_id: account_management?.account?.merchant_id,
      plan_id: planId,
      trial_plan: "no",
    };
    dispatch(updatePlanTopup(updateTopupData));
  };
  const changePosCountSubmitHandler = () => {
    console.log("changePosCountSubmitHandler:");
    const updatePosCountData = {
      merchant_id: account_management?.account?.merchant_id,
      pos_count: posCount,
      pos_plan: posPlanCostId,
    };
    dispatch(updatePosCount(updatePosCountData));
  };

  return (
    <UserInterface>
      <div className="no-sidebar row" id="content">
        <Headerbar type={"account-management"} />
        <div className="inner-content row mx-0">
          <div className="shadowclass container">
            <div className="d-flex justify-content-between">
              <h4 className="event-alert border0">Account Details</h4>
              <label className="text-bold">
                Status:
                <span className="text-green mx-1">
                  Due - $
                  {checkNull(
                    account_management?.account?.merchant
                      ?.merchant_purchased_plans?.cost,
                    false
                  )
                    ? parseFloat(
                        account_management?.account?.merchant
                          ?.merchant_purchased_plans?.cost
                      ).toFixed(2)
                    : ""}{" "}
                  by{" "}
                  {checkNull(
                    account_management?.account?.merchant
                      ?.merchant_purchased_plans?.expiry_date,
                    false
                  )
                    ? parseStringToDate(
                        account_management?.account?.merchant
                          ?.merchant_purchased_plans?.expiry_date,
                        "yyyy-MM-DD",
                        "DD MMM YY"
                      )
                    : ""}
                </span>
              </label>
            </div>
          </div>

          <div className="row mx-0">
            <div className="container px-0 d-flex">
              <div className="col-md-6 clearfix pr-3">
                <div className="shadowclass padding_b">
                  <form onSubmit={handleSubmit(onFormSubmit)}>
                    <ul className="ingredient-info edit-shift px-3">
                      <li>
                        <label className="">Company Name </label>
                        <div className="input-group col-7">
                          <input
                            className="required valid"
                            placeholder="Company Name"
                            maxlength="200"
                            type="text"
                            disabled="disabled"
                            value={inputForm?.company_name}
                            id="MerchantCompanyName"
                          />
                        </div>
                      </li>
                      <li>
                        <label className="">Account Name</label>
                        <div className="input-group col-7">
                          <input
                            {...register("account_name")}
                            className="required valid"
                            placeholder="Account Name"
                            maxlength="255"
                            type="text"
                            value={inputForm?.account_name}
                            onChange={(e) => {
                              changeInputHandler(
                                "account_name",
                                e?.target?.value
                              );
                            }}
                            id="MerchantAccountName"
                          />
                          <ErrorContainer errors={errors} name="account_name" />
                        </div>
                      </li>
                      <li>
                        <label className="">Timezone </label>
                        <div className="input-group select-div">
                          <input
                            className="required valid"
                            placeholder="Timezone"
                            maxlength="255"
                            type="text"
                            value={inputForm?.timezone}
                            id="MerchantTimezone"
                            disabled="disabled"
                          />
                          <ErrorContainer errors={errors} name="timezone" />
                        </div>
                      </li>
                      {user_details?.user?.merchant?.parent_merchant_id ==
                        0 && (
                        <>
                          <li>
                            <label className="">Parent Account </label>
                            <div className="input-group select-div span7">
                              <select
                                className="required valid"
                                {...register("is_parent")}
                                value={inputForm?.is_parent}
                                onChange={(e) => {
                                  changeInputHandler(
                                    "is_parent",
                                    e?.target?.value
                                  );
                                }}
                              >
                                <option value=""> Select </option>
                                <option value="0">No</option>
                                <option value="1">Yes</option>
                              </select>
                              <ErrorContainer
                                errors={errors}
                                name="is_parent"
                              />
                            </div>
                          </li>
                          <li>
                            <label className="">Group Admin </label>
                            <div className="input-group select-div span7">
                              <select
                                className="required valid"
                                {...register("is_group_admin")}
                                value={inputForm?.is_group_admin}
                                onChange={(e) => {
                                  changeInputHandler(
                                    "is_group_admin",
                                    e?.target?.value
                                  );
                                }}
                              >
                                <option value=""> Select </option>
                                <option value="0">No</option>
                                <option value="1">Yes</option>
                              </select>
                              <ErrorContainer
                                errors={errors}
                                name="is_group_admin"
                              />
                            </div>
                          </li>
                          <li>
                            <label className="">Enable Cluster </label>
                            <div className="input-group select-div span7">
                              <select
                                className="required valid"
                                {...register("enable_cluster")}
                                value={inputForm?.enable_cluster}
                                onChange={(e) => {
                                  changeInputHandler(
                                    "enable_cluster",
                                    e?.target?.value
                                  );
                                }}
                              >
                                <option value=""> Select </option>
                                <option value="0">No</option>
                                <option value="1">Yes</option>
                              </select>
                              <ErrorContainer
                                errors={errors}
                                name="enable_cluster"
                              />
                            </div>
                          </li>
                        </>
                      )}
                    </ul>

                    <div className="counts">
                      <h4 className="event-alert px-3">Address</h4>
                    </div>
                    <input
                      hidden
                      type="text"
                      {...register("latitude")}
                      value={inputForm?.latitude}
                    />
                    <input
                      hidden
                      type="text"
                      {...register("longitude")}
                      value={inputForm?.longitude}
                    />
                    <ul className="ingredient-info edit-shift px-3">
                      <li>
                        <label className="">Address Line #1 </label>
                        <div className="input-group col-7">
                          <input
                            {...register("address")}
                            className="required valid"
                            placeholder="Search on Map "
                            id="UserAddress"
                            maxlength="200"
                            type="text"
                            value={inputForm?.address}
                            onChange={(e) => {
                              changeInputHandler("address", e?.target?.value);
                            }}
                          />
                          <ErrorContainer errors={errors} name="address" />
                        </div>
                      </li>
                      <li>
                        <label className="">Address Line #2</label>
                        <div className="input-group col-7">
                          <input
                            className="valid"
                            id="UserAddress2"
                            maxlength="200"
                            type="text"
                            {...register("address_2")}
                            value={inputForm?.address_2}
                            onChange={(e) => {
                              changeInputHandler("address_2", e?.target?.value);
                            }}
                          />
                          <ErrorContainer errors={errors} name="address_2" />
                        </div>
                      </li>
                      <li>
                        <label className="">City </label>
                        <div className="input-group col-7">
                          <input
                            className="required valid"
                            placeholder="City"
                            id="City"
                            type="text"
                            {...register("city_id")}
                            value={inputForm?.city_id}
                            onChange={(e) => {
                              changeInputHandler("city_id", e?.target?.value);
                            }}
                          />
                          <ErrorContainer errors={errors} name="city_id" />
                        </div>
                      </li>
                      <li>
                        <label className="">Country </label>
                        <div className="input-group select-div span7">
                          <select
                            name="data[Merchant][country_id]"
                            className="required valid"
                            id="Country"
                            {...register("country_id")}
                            value={inputForm?.country_id}
                            onChange={(e) => {
                              changeInputHandler(
                                "country_id",
                                e?.target?.value
                              );
                            }}
                          >
                            <option value=""> Select Country </option>
                            {countryArr !== null &&
                              Object.keys(countryArr).map((index) => {
                                return (
                                  <option value={index} key={index}>
                                    {" "}
                                    {countryArr?.[index]}{" "}
                                  </option>
                                );
                              })}
                          </select>
                          <ErrorContainer errors={errors} name="country_id" />
                        </div>
                      </li>
                      <li>
                        <label className="">State </label>
                        <div className="input-group select-div span7">
                          <select
                            className="required valid"
                            id="State"
                            {...register("state_id")}
                            value={inputForm?.state_id}
                            onChange={(e) => {
                              changeInputHandler("state_id", e?.target?.value);
                            }}
                          >
                            <option value=""> Select State </option>
                            {stateArr !== null &&
                              Object.keys(stateArr).map((index) => {
                                return (
                                  <option value={index} key={index}>
                                    {" "}
                                    {stateArr?.[index]}{" "}
                                  </option>
                                );
                              })}
                          </select>
                          <ErrorContainer errors={errors} name="state_id" />
                        </div>
                      </li>
                      <li>
                        <label className="">Zipcode</label>
                        <div className="input-group col-7">
                          <input
                            className="required valid"
                            id="ZipCode"
                            maxlength="30"
                            type="text"
                            {...register("zip_code")}
                            value={inputForm?.zip_code}
                            onChange={(e) => {
                              changeInputHandler("zip_code", e?.target?.value);
                            }}
                          />
                          <ErrorContainer errors={errors} name="zip_code" />
                        </div>
                      </li>
                      <li>
                        <div className="btns row mx-0 justify-content-end">
                          <LoadingBtn
                            is_loading={is_add_btn_loading}
                            id=""
                            type="Submit"
                            className="col-3 mx-0"
                          >
                            Save
                          </LoadingBtn>
                        </div>
                      </li>
                    </ul>
                  </form>
                </div>
              </div>

              <div className="col-md-6 clearfix pl-3">
                <div className="shadowclass padding_b">
                  <ul className="ingredient-info edit-shift px-3">
                    <li>
                      <label className="">Administrator Name </label>
                      <div className="input-group">
                        {account_management?.adminInfo?.full_name ?? ""}
                      </div>
                    </li>
                    <li className="custom_d">
                      Warning: You will not be able to view this page once you
                      have changed the admin.
                      <button
                        className="btn btn-bordered account_admin"
                        onClick={() => closeAccAdminModal(true)}
                      >
                        Change Account Admin
                      </button>
                    </li>
                  </ul>
                  <div className="d-flex px-3">
                    <div className="w-100 position-relative map_sec mt-2">
                      {account_management !== null && (
                        <MyGoogleMap
                          mapChangeHandler={mapChangeHandler}
                          location={{
                            center: [
                              parseFloat(
                                account_management?.account?.merchant
                                  ?.latitude ?? 0
                              ),
                              parseFloat(
                                account_management?.account?.merchant
                                  ?.longitude ?? 0
                              ),
                            ],
                            lat: parseFloat(
                              account_management?.account?.merchant?.latitude ??
                                0
                            ),
                            lng: parseFloat(
                              account_management?.account?.merchant
                                ?.longitude ?? 0
                            ),
                          }}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="row mx-0">
            <div className="container px-0 d-flex flex-wrap">
              <div className="col-md-8 pr-3">
                <div className="shadowclass">
                  <div className="counts">
                    <h4 className="event-alert ">Current Plan</h4>
                  </div>
                  <ul className="ingredient-info edit-shift px-0">
                    <li>
                      <label className="">Current Plan </label>
                      <div className="input-group">
                        $
                        {checkNull(
                          account_management?.account?.merchant
                            ?.merchant_purchased_plans?.cost,
                          false
                        )
                          ? parseFloat(
                              account_management?.account?.merchant
                                ?.merchant_purchased_plans?.cost
                            ).toFixed(2)
                          : 0}{" "}
                        {checkNull(
                          account_management?.account?.merchant
                            ?.merchant_purchased_plans?.plan_name
                        )}
                        <a
                          className="btn btn-bordered pull-right change_plans"
                          onClick={() => closeChangePlanModal(true)}
                        >
                          Change Plan
                        </a>
                      </div>
                    </li>
                    <li>
                      <label className="">Current Topup </label>
                      <div className="input-group">
                        {checkNull(
                          account_management?.account?.merchant
                            ?.merchant_purchased_plans?.plan_name,
                          false
                        )
                          ? `${
                              account_management?.account?.merchant
                                ?.merchant_purchased_plans?.plan_name
                            } + ${
                              account_management?.account?.merchant
                                ?.merchant_purchased_plans?.employee_chef ==
                              "unlimited"
                                ? "Unlimited"
                                : account_management?.account?.merchant
                                    ?.merchant_purchased_plans?.employee_chef
                            } Employees`
                          : ""}
                        <a
                          className="btn btn-bordered pull-right change_plan"
                          onClick={() => closeChangeTopupModal(true)}
                        >
                          Change Topup
                        </a>
                      </div>
                    </li>
                    <li>
                      <label className="">POS Terminals </label>
                      <div className="input-group">
                        {checkNull(
                          account_management?.account?.merchant
                            ?.total_pos_terminals
                        )}
                        <a
                          className="btn btn-bordered pull-right posbutton"
                          onClick={() => closeChangePosCountModal(true)}
                        >
                          Change POS
                        </a>
                      </div>
                    </li>
                    <li>
                      <label className=""> Add Ons </label>
                      <div className="input-group">
                        {account_management?.purchasedPlans?.[5]?.pos_plan ||
                        account_management?.purchasedPlans?.[6]?.pos_plan
                          ? "Ordrr"
                          : "NA"}{" "}
                      </div>
                    </li>
                    <li>
                      <label className="">Plan Price </label>
                      <div className="input-group">
                        USD
                        {checkNull(
                          account_management?.account?.merchant
                            ?.merchant_purchased_plans?.cost,
                          false
                        )
                          ? parseFloat(
                              account_management?.account?.merchant
                                ?.merchant_purchased_plans?.cost
                            ).toFixed(2)
                          : 0}{" "}
                        Per Month
                      </div>
                    </li>
                    <li>
                      <label className="">Account Opening Date </label>
                      <div className="input-group">
                        {checkNull(
                          account_management?.account?.merchant?.created_at,
                          false
                        )
                          ? parseStringToDate(
                              account_management?.account?.merchant?.created_at,
                              null,
                              "DD/MM/yyyy"
                            )
                          : ""}{" "}
                      </div>
                    </li>
                    <li>
                      <label className="">Payment Setup </label>
                      <div className="input-group">Monthly Recurring </div>
                    </li>
                  </ul>

                  <div className="inner-space screen-scroll mCS_destroyed">
                    <table
                      width="100%"
                      cellspacing="0"
                      cellpadding="0"
                      className="checklist-table remove-space inventory_table"
                    >
                      <thead>
                        <tr>
                          <th width="">User Type</th>
                          <th width=""># of Users in Plan </th>
                          <th width=""># of Active Users </th>
                          <th width=""># of Accounts Remaining</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>Admin</td>
                          <td>
                            {checkNull(
                              account_management?.account?.merchant
                                ?.merchant_purchased_plans?.store_entities,
                              false
                            )
                              ? account_management?.account?.merchant
                                  ?.merchant_purchased_plans?.store_entities
                              : 0}
                          </td>
                          <td>1</td>
                          <td>0</td>
                        </tr>
                        <tr>
                          <td>Owner/Manager</td>
                          <td>
                            {account_management?.account?.merchant
                              ?.merchant_purchased_plans?.owner_managers ?? 0}
                          </td>
                          <td>
                            {account_management?.users_count_data
                              ?.owner_managers ?? 0}
                          </td>
                          <td>
                            {parseFloat(
                              account_management?.account?.merchant
                                ?.merchant_purchased_plans?.owner_managers ?? 0
                            ) -
                              parseFloat(
                                account_management?.users_count_data
                                  ?.owner_managers ?? 0
                              )}
                          </td>
                        </tr>
                        <tr>
                          <td>Chef/Employee </td>
                          <td>
                            {account_management?.account?.merchant
                              ?.merchant_purchased_plans?.employee_chef ?? 0}
                          </td>
                          <td>
                            {account_management?.users_count_data
                              ?.employee_chef ?? 0}
                          </td>
                          <td>
                            {parseFloat(
                              account_management?.account?.merchant
                                ?.merchant_purchased_plans?.employee_chef ?? 0
                            ) -
                              parseFloat(
                                account_management?.users_count_data
                                  ?.employee_chef ?? 0
                              )}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
              <div class="col-md-4 pl-3">
                <form
                  onSubmit={savePaymentHandleSubmit(onSavePaymentFormSubmit)}
                >
                  <div class="shadowclass padding_b">
                    <div class="counts">
                      <h4 class="event-alert ">Payment Details</h4>
                    </div>

                    <ul class="ingredient-info edit-shift">
                      <li>
                        <label class="">Payment Owner </label>
                        <div class="input-group select-div span7">
                          <select
                            {...savePaymentReg("account_owner")}
                            class="required valid payment_owner"
                            id="MerchantAccountOwner"
                            onChange={(e) =>
                              changeSavePaymentInputHandler(
                                "account_owner",
                                e?.target?.value ?? ""
                              )
                            }
                            value={savePaymentForm?.account_owner}
                          >
                            <option value="">Select Payment Owner</option>
                            {account_management?.owners &&
                              account_management?.owners !== null &&
                              Object.keys(account_management?.owners).map(
                                (owner) => {
                                  return (
                                    <option value={owner}>
                                      {account_management?.owners?.[owner] ??
                                        ""}
                                    </option>
                                  );
                                }
                              )}
                          </select>{" "}
                          <ErrorContainer
                            errors={savePaymentErrors}
                            name="account_owner"
                          />
                        </div>
                      </li>
                      <li>
                        <label class="">Payment Account </label>
                        <div class="input-group select-div span7">
                          <select
                            {...savePaymentReg("payment_account")}
                            class="required valid"
                            id="MerchantPaymentAccount"
                            onChange={(e) =>
                              changeSavePaymentInputHandler(
                                "payment_account",
                                e?.target?.value ?? ""
                              )
                            }
                            value={savePaymentForm?.payment_account}
                          >
                            <option value="">Select Payment Account</option>
                            {checkArray(account_management?.subAccounts) &&
                              account_management?.subAccounts.map(
                                (subAccount) => {
                                  return (
                                    <option value={subAccount?.id}>
                                      {subAccount?.name ?? ""}
                                    </option>
                                  );
                                }
                              )}
                          </select>{" "}
                          <ErrorContainer
                            errors={savePaymentErrors}
                            name="payment_account"
                          />
                        </div>
                      </li>
                    </ul>
                  </div>
                  {console.log("savePaymentErrors", savePaymentErrors)}
                  <div class="shadowclass padding_b">
                    <div class="counts">
                      <h4 class="event-alert ">GTO Reporting</h4>
                    </div>
                    <ul class="ingredient-info edit-shift">
                      <li>
                        <label class="">Landlord Name </label>
                        <div class="input-group select-div">
                          <select
                            {...savePaymentReg("landlord_name")}
                            class="valid"
                            id="GtoReportingLandlordName"
                            onChange={(e) =>
                              changeSavePaymentInputHandler(
                                "landlord_name",
                                e?.target?.value ?? ""
                              )
                            }
                            value={savePaymentForm?.landlord_name}
                          >
                            <option value="">Select Landlord Name</option>
                            {checkNull(
                              account_management?.gtoReporting?.landLordNames,
                              false
                            ) &&
                              Object.keys(
                                account_management?.gtoReporting?.landLordNames
                              ).map((landlord) => {
                                return (
                                  <option value={landlord}>
                                    {account_management?.gtoReporting
                                      ?.landLordNames?.[landlord] ?? ""}
                                  </option>
                                );
                              })}
                          </select>{" "}
                          <ErrorContainer
                            errors={savePaymentErrors}
                            name="landlord_name"
                          />
                        </div>
                      </li>
                      <li>
                        <label class="">Delivery Type </label>
                        <div class="input-group select-div">
                          <select
                            {...savePaymentReg("delivery_type")}
                            class="valid"
                            id="delivery_type"
                            onChange={(e) =>
                              changeSavePaymentInputHandler(
                                "delivery_type",
                                e?.target?.value ?? ""
                              )
                            }
                            value={savePaymentForm?.delivery_type}
                          >
                            <option value="">Select Delivery Type</option>
                            {checkNull(
                              account_management?.gtoReporting?.deliveryTypes,
                              false
                            ) &&
                              Object.keys(
                                account_management?.gtoReporting?.deliveryTypes
                              ).map((deliveryType) => {
                                return (
                                  <option value={deliveryType}>
                                    {account_management?.gtoReporting
                                      ?.deliveryTypes?.[deliveryType] ?? ""}
                                  </option>
                                );
                              })}
                          </select>{" "}
                          <ErrorContainer
                            errors={savePaymentErrors}
                            name="delivery_type"
                          />
                        </div>
                      </li>
                      {savePaymentForm?.delivery_type == "1" && (
                        <li class="delivery_type_1">
                          <label class="">Email Address </label>
                          <div class="input-group">
                            <MultipleValueTextInput
                              onItemAdded={(item, allItems) => {
                                console.log(`Item added: ${item}`);
                                changeSavePaymentInputHandler("email_address", [
                                  ...savePaymentForm.email_address,
                                  item,
                                ]);
                              }}
                              onItemDeleted={(item, allItems) => {
                                console.log(`Item removed: ${item}`);
                                changeSavePaymentInputHandler(
                                  "email_address",
                                  savePaymentForm.email_address.filter(
                                    (p) => p != item
                                  )
                                );
                              }}
                              values={savePaymentForm?.email_address}
                              label=""
                              name="item-input"
                              charCodes={[13, 32]}
                              placeholder=""
                            />
                          </div>
                          {checkArray(savePaymentErrors.email_address) ? (
                            savePaymentErrors.email_address.map(
                              (emailErr, emailInd) => {
                                return (
                                  <ErrorContainer
                                    errors={savePaymentErrors.email_address}
                                    name={emailInd}
                                  />
                                );
                              }
                            )
                          ) : (
                            <ErrorContainer
                              errors={savePaymentErrors}
                              name="email_address"
                            />
                          )}
                        </li>
                      )}
                      {savePaymentForm?.delivery_type == "2" && (
                        <>
                          <li class="delivery_type_2 hide">
                            <label class="">SFTP Address</label>
                            <div class="input-group">
                              <input
                                {...savePaymentReg("sftp_address")}
                                class="valid"
                                placeholder="Enter SFTP Address"
                                id="sftp_address"
                                type="text"
                                onChange={(e) =>
                                  changeSavePaymentInputHandler(
                                    "sftp_address",
                                    e?.target?.value ?? ""
                                  )
                                }
                                value={savePaymentForm?.sftp_address}
                              />{" "}
                            </div>
                          </li>

                          <li class="delivery_type_2 hide">
                            <label class="">SFTP User</label>
                            <div class="input-group">
                              <input
                                {...savePaymentReg("sftp_user")}
                                class="valid"
                                placeholder="Enter SFTP User"
                                id="sftp_user"
                                type="text"
                                autoComplete="new-password"
                                onChange={(e) =>
                                  changeSavePaymentInputHandler(
                                    "sftp_user",
                                    e?.target?.value ?? ""
                                  )
                                }
                                value={savePaymentForm?.sftp_user}
                              />{" "}
                            </div>
                          </li>

                          <li class="delivery_type_2 hide">
                            <label class="">SFTP Password</label>
                            <div class="input-group">
                              <input
                                {...savePaymentReg("sftp_password")}
                                class="valid input-txt"
                                placeholder="Enter SFTP Password"
                                id="sftp_password"
                                type="password"
                                autoComplete="new-password"
                                onChange={(e) =>
                                  changeSavePaymentInputHandler(
                                    "sftp_password",
                                    e?.target?.value ?? ""
                                  )
                                }
                                value={savePaymentForm?.sftp_password}
                              />{" "}
                            </div>
                          </li>
                        </>
                      )}

                      <li class="">
                        <label class="">Tenant ID</label>
                        <div class="input-group">
                          <input
                            {...savePaymentReg("tenant_id")}
                            name="data[GtoReporting][tenant_id]"
                            class="valid input-txt"
                            placeholder="Tenant ID"
                            id="tenant_id"
                            type="text"
                            onChange={(e) =>
                              changeSavePaymentInputHandler(
                                "tenant_id",
                                e?.target?.value ?? ""
                              )
                            }
                            value={savePaymentForm?.tenant_id}
                          />{" "}
                        </div>
                      </li>

                      <li class="">
                        <label class="">Batch Sequence Starting Number</label>
                        <div class="input-group">
                          <input
                            {...savePaymentReg("bssn")}
                            class="valid input-txt"
                            placeholder="Batch Sequence Starting Number"
                            id="bssn"
                            type="number"
                            disabled={
                              checkNull(
                                account_management?.account?.GtoReporting?.bssn,
                                false
                              )
                                ? true
                                : false
                            }
                            onChange={(e) =>
                              changeSavePaymentInputHandler(
                                "bssn",
                                e?.target?.value ?? ""
                              )
                            }
                            value={savePaymentForm?.bssn}
                          />{" "}
                        </div>
                      </li>
                    </ul>
                  </div>
                  <button
                    type="submit"
                    ref={savePaymentBtn}
                    style={{ display: "none" }}
                  >
                    submit
                  </button>
                </form>
              </div>
            </div>
          </div>
          <div className="row mx-0">
            <div className="container px-0 d-flex col-12">
              <div class="btns col-4 d-flex justify-content-end py-4 col-12">
                <LoadingBtn
                  is_loading={is_save_payment_loading}
                  id=""
                  type="button"
                  onClick={() => savePaymentBtn?.current.click()}
                >
                  Save
                </LoadingBtn>
                <button
                  id="cancel"
                  type="button"
                  onClick={() => initialSetupPaymentState()}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>

          <div className="row mx-0">
            <div className="container px-0 d-flex">
              <div className="col-md-8 pr-3">
                <div class="shadowclass">
                  <div class="counts">
                    <h4 class="event-alert ">Account Relationships</h4>
                  </div>
                  <div class="inner-space screen-scroll mCS_destroyed">
                    <table
                      width="100%"
                      cellspacing="0"
                      cellpadding="0"
                      class="checklist-table remove-space inventory_table"
                    >
                      <thead>
                        <tr>
                          <th width="">Account Name</th>
                          <th width="">Same Entity?</th>
                          <th width=""># of Shared Bank Accounts</th>
                        </tr>
                      </thead>
                      <tbody>
                        {checkArray(account_management?.relations) &&
                          account_management?.relations.map((relation) => {
                            return (
                              <tr>
                                <td>{relation?.name}</td>
                                <td>{relation?.sheet}</td>
                                <td>{relation?.ac}</td>
                              </tr>
                            );
                          })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
              <div class="col-md-4 pl-3">
                <div class="shadowclass">
                  <div class="counts">
                    <h4 class="event-alert ">Invoice History</h4>
                  </div>
                  <div class="inner-space screen-scroll mCS_destroyed">
                    <table
                      width="100%"
                      cellspacing="0"
                      cellpadding="0"
                      class="checklist-table remove-space inventory_table"
                    >
                      <thead>
                        <tr>
                          <th width="">Account Name</th>
                          <th width="">Date</th>
                          <th width="">Amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {checkArray(account_management?.invoices?.data) &&
                          account_management?.invoices?.data.map((invoice) => {
                            return (
                              <tr>
                                <td>
                                  {account_management?.account?.merchant
                                    ?.account_name ?? ""}
                                  {/* todo */}
                                </td>
                                <td>
                                  {invoice?.created
                                    ? moment
                                        .unix(invoice?.created)
                                        .format("MM-DD-yyyy")
                                    : ""}
                                </td>
                                <td>
                                  {invoice?.amount_paid
                                    ? parseFloat(
                                        invoice?.amount_paid / 100
                                      ).toFixed(2)
                                    : "0.00"}
                                </td>
                              </tr>
                            );
                          })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="row mx-0">
            <div className="container px-0 d-flex">
              <div class="shadowclass padding_b">
                <div class="counts">
                  <h4 class="event-alert ">Account Cancellation</h4>
                </div>
                <p>
                  We’re sorry to see you leave. Once your account is cancelled
                  you won’t be charged again after your official cancellation
                  date and all user access within the account will be revoked
                  upon the end of the paid-up period. You can always activate
                  this account again by contacting us.
                </p>
                <button
                  class="btn btn-bordered blueborder cancel_account"
                  onClick={() => closeCancelAccModal(true)}
                >
                  Cancel Account
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <ModelContainer
        title={`${
          showGroupList
            ? "Please select a role for you here on"
            : "Select account admin"
        }`}
        open={accAdminModal}
        closeModal={closeAccAdminModal}
        submitModal={accAdminHandleSubmit(adminAccSubmitHandler)}
        is_loading={is_accAdmin_modal_loading}
      >
        <form>
          {!showGroupList && (
            <ul className="form-list label-group">
              {account_management?.adminUsers &&
              account_management?.adminUsers !== null ? (
                Object.keys(account_management?.adminUsers).map((index) => {
                  return (
                    <li>
                      <div class="input-wrap custom_row">
                        <label
                          class="radio"
                          onClick={(e) => {
                            changeAccAdminInputHandler("admin_id", index);
                          }}
                          for={`radio${index}`}
                        >
                          {account_management?.adminUsers?.[index]}
                          <input
                            type="radio"
                            {...accAdminReg("admin_id")}
                            value={index}
                            checked={accAdminForm?.admin_id == index}
                            id={`radio${index}`}
                            required="true"
                          />
                          <span class="checkround"></span>
                        </label>
                      </div>
                    </li>
                  );
                })
              ) : (
                <li>
                  <div class="input-wrap">No other admin users available</div>
                </li>
              )}
              <div className="row">
                <div className="col-12">
                  <ErrorContainer errors={accAdminErrors} name="admin_id" />
                </div>
              </div>
            </ul>
          )}
          {showGroupList && (
            <ul className="form-list label-group">
              {account_management?.groups_list &&
              account_management?.groups_list !== null ? (
                Object.keys(account_management?.groups_list).map((index) => {
                  return (
                    <li>
                      <div class="input-wrap custom_row">
                        <label class="radio">
                          {account_management?.groups_list?.[index]}
                          <input
                            type="radio"
                            {...accAdminReg("group_id")}
                            value={index}
                            onClick={(e) => {
                              changeAccAdminInputHandler("group_id", index);
                            }}
                            checked={accAdminForm?.group_id == index}
                            id={`radio${index}`}
                            required="true"
                          />
                          <span class="checkround"></span>
                        </label>
                      </div>
                    </li>
                  );
                })
              ) : (
                <li>
                  <div class="input-wrap">No group available</div>
                </li>
              )}
              <div className="row">
                <div className="col-12">
                  <ErrorContainer errors={accAdminErrors} name="group_id" />
                </div>
              </div>
            </ul>
          )}
        </form>
      </ModelContainer>
      <ModelContainer
        title={`Select Plan`}
        open={changePlanModal}
        closeModal={closeChangePlanModal}
        submitModal={changePlanSubmitHandler}
        is_loading={is_changePlan_modal_loading}
      >
        <form
          class=""
          id="UserAccountManagementFormForm"
          method="post"
          accept-charset="utf-8"
        >
          <fieldset class="">
            <ul class="form-list label-group">
              {account_management?.posPlans &&
                account_management?.posPlans.length > 0 &&
                account_management?.posPlans
                  .filter((p) => p.id != 2)
                  .map((plan) => {
                    return plan?.pos_plan_costs &&
                      plan.pos_plan_costs.length > 0
                      ? plan.pos_plan_costs.map((cost) => {
                          return (
                            <li class="funkyradio">
                              <div
                                class="funkyradio-primary"
                                onClick={() => {
                                  setPosPlanCostId(cost?.id);
                                  setCurrentPlanCost(cost?.cost);
                                }}
                              >
                                <input
                                  type="radio"
                                  name="pos_plan_cost_id"
                                  id={`plan${cost?.id}`}
                                  value={cost?.id}
                                  checked={cost?.id == posPlanCostId}
                                />
                                <input
                                  type="hidden"
                                  name="current_plan_cost"
                                  value={cost?.cost}
                                />
                                <label for={`plan${cost?.id}`}>
                                  <div class="top_div">
                                    <span>
                                      Name: <strong>{cost?.name}</strong>
                                    </span>{" "}
                                    <span class="pull-right mr-0">
                                      Cost: <strong>${cost?.cost}</strong>
                                    </span>
                                  </div>
                                  <div class="bottom_div">
                                    <span>
                                      Duration: <strong>{cost?.type}</strong>
                                    </span>
                                  </div>
                                  <div class="bottom_div">
                                    <span>
                                      Description: <strong>{cost?.desc}</strong>
                                    </span>
                                  </div>
                                </label>
                              </div>
                            </li>
                          );
                        })
                      : null;
                  })}
            </ul>
          </fieldset>
        </form>
      </ModelContainer>
      <ModelContainer
        title={`Select Topup`}
        open={changeTopupModal}
        closeModal={closeChangeTopupModal}
        submitModal={changeTopupSubmitHandler}
        is_loading={is_changeTopup_modal_loading}
      >
        <form
          class=""
          id="UserAccountManagementFormForm"
          method="post"
          accept-charset="utf-8"
        >
          <fieldset>
            <ul class="form-list label-group">
              {account_management?.plan_list &&
                account_management?.plan_list.length > 0 &&
                account_management?.plan_list.map((plan) => {
                  return (
                    <li class="funkyradio">
                      <div
                        class="funkyradio-primary"
                        onClick={() => {
                          setPlanId(plan?.id);
                        }}
                      >
                        <input
                          type="radio"
                          name="plan_id"
                          id={`mplan${plan?.id}`}
                          value={plan?.id}
                          checked={planId == plan?.id}
                        />
                        <label for={`mplan${plan?.id}`}>
                          <div class="top_div">
                            <span>
                              Name: <strong>{plan?.name}</strong>
                            </span>{" "}
                            <span class="pull-right mr-0">
                              Cost: <strong>${plan?.cost}</strong>
                            </span>
                          </div>
                          <div class="bottom_div">
                            <span>
                              Duration: <strong>{plan?.duration}</strong>
                            </span>
                            <span>
                              Modules: <strong>All</strong>
                            </span>
                            <span className="text-align-right">
                              Store entities:{" "}
                              <strong>{plan?.store_entities}</strong>
                            </span>
                          </div>
                          <div class="bottom_div">
                            <span>
                              Admin: <strong>{plan?.admin}</strong>
                            </span>
                            <span>
                              Owner managers:{" "}
                              <strong>
                                {plan?.owner_managers == 100000
                                  ? "Unlimited"
                                  : plan?.owner_managers}
                              </strong>
                            </span>{" "}
                            <span className="text-align-right">
                              Emplyee: chef{" "}
                              <strong>
                                {plan?.employee_chef == 100000
                                  ? "Unlimited"
                                  : plan?.employee_chef}
                              </strong>
                            </span>
                          </div>
                        </label>
                      </div>
                    </li>
                  );
                })}
            </ul>
          </fieldset>
        </form>
      </ModelContainer>
      <ModelContainer
        title={`POS TERMINALS | Add # of POS Terminals Required for Your Store (Including Kitchen Order Terminals)`}
        open={changePosCountModal}
        closeModal={closeChangePosCountModal}
        submitModal={changePosCountSubmitHandler}
        is_loading={is_changePosCount_modal_loading}
      >
        <form
          id="form_step_zero"
          class=""
          enctype="multipart/form-data"
          method="post"
          accept-charset="utf-8"
        >
          <div class="table-responsive2">
            <table class="table">
              <thead>
                <tr>
                  <th>Order item</th>
                  <th>Qty</th>
                  <th>Each</th>
                  <th>Total</th>
                  <th>Frequency</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Terminals</td>
                  <td>
                    <input
                      type="number"
                      name="pos_count"
                      value={posCount}
                      class="pos_count required"
                      required="true"
                      min="1"
                      step="1"
                      onChange={(e) =>
                        setPosCount(
                          e?.target?.value ? parseInt(e?.target?.value) : 0
                        )
                      }
                    />
                  </td>
                  <td class="eachid">{currentPlanCost}</td>
                  <td class="totalid">
                    {parseFloat(checkNull(posCount, false) ? posCount : 0) *
                      parseFloat(
                        checkNull(currentPlanCost, false) ? currentPlanCost : 0
                      )}
                  </td>
                  <td class="freqid">Frequency</td>
                </tr>
              </tbody>
            </table>
          </div>
        </form>
      </ModelContainer>
      <ConfirmModel
        title="Sorry to see you go"
        text="Are you sure, you want to cancel your account?"
        open={cancelAccModal}
        closeModal={closeCancelAccModal}
        submitModal={submitCancelAccHandler}
        is_loading={is_cancel_account_loading}
      />
    </UserInterface>
  );
};
export default AccountManagement;
